import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { QueryClient } from '@tanstack/react-query';
import color from 'color-string';
import type { UploadedFile } from '@r/types/index.ts';
import type { RouteRouteReadModel } from '@r/api/generated/client.schemas.ts';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 0,
      staleTime: 5 * 1000
    }
  }
});

export function downloadCSV(csv: string, filename = 'download.csv') {
  const BOM = '\uFEFF';
  const csvData = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });

  const tempLink = document.createElement('a');
  tempLink.href = window.URL.createObjectURL(csvData);
  tempLink.setAttribute('download', filename);
  tempLink.click();
}

export const linksAvailable = (
  availableRoutes: RouteRouteReadModel[] | undefined,
  routes: string[]
): string[] => {
  if (!availableRoutes) return [];
  return routes.filter((route) =>
    availableRoutes.some((availableRoute) => availableRoute.path === route)
  );
};

export const updateFavicon = (link: string) => {
  const favicon = document.querySelector<HTMLAnchorElement>('link[rel="icon"]');

  if (favicon !== null) {
    favicon.href = link;
  } else {
    const newFavicon = document.createElement('link');
    newFavicon.rel = 'icon';
    newFavicon.href = link;
    document.head.appendChild(newFavicon);
  }
};

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: 'accurate' | 'normal';
  } = {}
) {
  const { decimals = 0, sizeType = 'normal' } = opts;

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const accurateSizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB'];
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(decimals)} ${
    sizeType === 'accurate' ? (accurateSizes[i] ?? 'Bytest') : (sizes[i] ?? 'Bytes')
  }`;
}

export function colorValidator(val: string) {
  try {
    return color.get.hsl(val);
  } catch {
    return false;
  }
}

export function isProd() {
  return import.meta.env.PROD;
}

export const getTypeFromExtension = (url: string): string => {
  const extension = url.split('.').pop()?.toLowerCase() || '';
  const mimeTypes: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml'
  };
  return mimeTypes[extension];
};

export const isUploadedFileWithPreview = (file: UploadedFile): boolean => {
  return file.type.startsWith('image');
};

export function extractFilename(url: string): string {
  const urlPath = url.split('?')[0];
  const segments = urlPath.split('/');
  return segments[segments.length - 1];
}

export const repeat = (times: number) => {
  return Array.from(Array(times).keys());
};
